///
/// EarthBrowser - GeoJSON File Handler
///
/// Handles GeoJSON file drag & drop
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { registerDatasetSourceFile } from '@/components/features/user-datasets/datasetSourceFiles'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { MemoryDataProvider } from '@/data-providers/MemoryDataProvider'
import type { DataRow } from '@/data-providers/types'
import { getOrCreateDatasetWindow } from '@/utils/windowUtils'
import type { GeoJSONParseDataChunk, GeoJSONParseProgress, GeoJSONWorkerType } from '@/workers/geojson/index'
import GeoJSONWorker from '@/workers/geojson/index.ts?worker'
import { proxy, wrap, type Remote } from 'comlink'
import { offerPlacemarkFromPoints } from '../placemarkFromPoints'
import { BaseFileHandler } from './BaseFileHandler'

export class GeoJSONFileHandler extends BaseFileHandler {
	private workerRef: Remote<GeoJSONWorkerType> | null = null
	private worker: Worker | null = null

	canHandle(file: File): boolean {
		const fileName = file.name.toLowerCase()
		return fileName.endsWith('.geojson') || fileName.endsWith('.json')
	}

	// No-op - worker is created on demand
	initialize(): void {}

	/**
	 * Ensures the worker is created, creating it on-demand if needed
	 */
	private ensureWorker(): void {
		if (!this.worker) {
			this.worker = new GeoJSONWorker()
			this.workerRef = wrap<GeoJSONWorkerType>(this.worker)
		}
	}

	cleanup(): void {
		if (this.worker) {
			this.worker.terminate()
			this.worker = null
			this.workerRef = null
		}
	}

	/** Returns the created window id (null when the file is already open) */
	async handleFile(file: File, options?: { openDrawer?: boolean }): Promise<string | null> {
		// Create worker on-demand
		this.ensureWorker()

		if (!this.workerRef) {
			console.error('GeoJSON worker could not be created')
			return null
		}

		// Get or create window for this file
		const windowId = getOrCreateDatasetWindow(
			this.context.getState,
			this.context.dispatch,
			file.name,
			'geojson',
			{ width: 700, height: 500, openDrawer: options?.openDrawer }
		)

		// If window already exists, do nothing
		if (!windowId) {
			return null
		}

		try {
			// Accumulate data chunks as they arrive
			const allData: Record<string, unknown>[] = []

			// Create MemoryDataProvider with empty data initially
			const provider = new MemoryDataProvider({
				id: windowId,
				name: file.name,
				data: [],
			})

			// Register the provider immediately - this makes it available to the window
			DataProviderRegistry.register(windowId, provider)

			// Parse GeoJSON in worker with progress reporting and chunked data transfer
			await this.workerRef.parseFile(
				file,
				proxy((progress: GeoJSONParseProgress) => {
					// Dispatch progress event for the window to listen to
					window.dispatchEvent(new CustomEvent(`dataset:progress:${windowId}`, {
						detail: {
							windowId,
							filename: file.name,
							processedRows: progress.featuresParsed,
							percentage: progress.percentage
						}
					}))
				}),
				proxy((dataChunk: GeoJSONParseDataChunk) => {
					// Accumulate data as it arrives from worker
					allData.push(...dataChunk.chunk)
				})
			)

			// Update provider with all the parsed data
			const finalProvider = new MemoryDataProvider({
				id: windowId,
				name: file.name,
				data: allData as DataRow[],
			})

			// Re-register with the complete data
			DataProviderRegistry.register(windowId, finalProvider)

			// Keep the original file around for cloud upload (before the
			// complete event so the browser toolbar sees it on first render)
			registerDatasetSourceFile(windowId, { file, format: 'geojson', featureCount: allData.length })

			// Dispatch completion event for the window to listen to
			window.dispatchEvent(new CustomEvent(`dataset:complete:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					rowCount: allData.length
				}
			}))

			console.log(`GeoJSON file loaded: ${file.name} (${allData.length} features)`)

			// Small point-only files can become a placemark instead
			offerPlacemarkFromPoints(this.context.dispatch, windowId, file.name, allData, 'geojson')
			return windowId
		} catch (error) {
			console.error('Failed to load GeoJSON:', error)

			// Dispatch error event
			window.dispatchEvent(new CustomEvent(`dataset:error:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					error: error instanceof Error ? error.message : String(error)
				}
			}))

			throw error
		}
	}
}

